import { Button, CheckboxField, IntegerField, TextField } from "@dash/form";
import { useTheme } from "@emotion/react";
import React from "react";

import { useClientManager } from "@megaron/react-clients";

type FormState = {
  email: string;
  imapHost: string;
  imapPort: number;
  imapTls: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpTls: boolean;
  username: string;
  password: string;
};

const defaultForm: FormState = {
  email: "",
  imapHost: "",
  imapPort: 993,
  imapTls: true,
  smtpHost: "",
  smtpPort: 465,
  smtpTls: true,
  username: "",
  password: "",
};

type InitialValues = Omit<FormState, "password">;

type Props = {
  onSuccess: () => void;
  onCancel: () => void;
  initialValues?: InitialValues;
};

const normalizePort = (value: number): number => Number(String(value).trim());

const sanitizeForm = (form: FormState): FormState => ({
  email: form.email.trim(),
  imapHost: form.imapHost.trim(),
  imapPort: normalizePort(form.imapPort),
  imapTls: form.imapTls,
  smtpHost: form.smtpHost.trim(),
  smtpPort: normalizePort(form.smtpPort),
  smtpTls: form.smtpTls,
  username: form.username.trim(),
  password: form.password.trim(),
});

export const ImapConnectForm: React.FC<Props> = ({ onSuccess, onCancel, initialValues }) => {
  const theme = useTheme();
  const support = useClientManager("support");
  const isEditing = !!initialValues;

  const [form, setForm] = React.useState<FormState>(() =>
    initialValues ? { ...initialValues, password: "" } : defaultForm,
  );

  const connectMutation = support.imapConnect().useMutation({ onSuccess });

  const set = <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = () => {
    const sanitized = sanitizeForm(form);
    if (!sanitized.password && !isEditing) return;
    if (isEditing && !sanitized.password) {
      connectMutation.mutate({ ...sanitized, password: "" });
    } else {
      connectMutation.mutate(sanitized);
    }
  };

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        maxWidth: "26rem",
        padding: "1rem",
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.mediumBorderRadius,
      }}
    >
      <TextField
        label="Adres email skrzynki"
        value={form.email}
        onChange={set("email")}
        isDisabled={isEditing}
      />
      <TextField label="Użytkownik (login)" value={form.username} onChange={set("username")} />
      <TextField
        label={isEditing ? "Nowe hasło (zostaw puste, aby nie zmieniać)" : "Hasło"}
        value={form.password}
        onChange={set("password")}
        inputElementProps={{ type: "password" }}
      />
      <div css={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
        <TextField label="Serwer IMAP" value={form.imapHost} onChange={set("imapHost")} css={{ flex: 1 }} />
        <IntegerField label="Port" value={form.imapPort} onChange={set("imapPort")} css={{ width: "5rem" }} />
        <CheckboxField label="TLS" value={form.imapTls} onChange={set("imapTls")} />
      </div>
      <div css={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
        <TextField label="Serwer SMTP" value={form.smtpHost} onChange={set("smtpHost")} css={{ flex: 1 }} />
        <IntegerField label="Port" value={form.smtpPort} onChange={set("smtpPort")} css={{ width: "5rem" }} />
        <CheckboxField label="TLS" value={form.smtpTls} onChange={set("smtpTls")} />
      </div>
      {connectMutation.isError && (
        <span css={{ fontSize: "0.875rem", color: theme.colors.danger }}>
          {String(connectMutation.error).includes("EncryptionKeyNotConfigured")
            ? "Klucz szyfrowania nie jest skonfigurowany na serwerze (SUPPORT_CREDENTIAL_ENCRYPTION_KEY)."
            : String(connectMutation.error).includes("CredentialDecryptionFailed")
              ? "Nie można odszyfrować zapisanego hasła. Sprawdź SUPPORT_CREDENTIAL_ENCRYPTION_KEY lub wprowadź hasło ponownie i zapisz."
              : String(connectMutation.error).includes("SmtpConnectionFailed")
              ? "Nie można połączyć się z serwerem SMTP. Sprawdź host, port i ustawienia TLS dla SMTP."
              : String(connectMutation.error).includes("ConnectionFailed")
                ? "Nie można połączyć się z serwerem IMAP. Sprawdź dane logowania i spróbuj ponownie."
                : "Nie można zapisać ustawień. Sprawdź dane i spróbuj ponownie."}
        </span>
      )}
      <div css={{ display: "flex", gap: "0.75rem" }}>
        <Button
          size="small"
          variant="primary"
          isLoading={connectMutation.isLoading}
          onClick={handleSubmit}
        >
          {isEditing ? "Zapisz zmiany" : "Połącz"}
        </Button>
        <Button size="small" variant="outline" onClick={onCancel}>
          Anuluj
        </Button>
      </div>
    </div>
  );
};
